import { UserType } from 'src/constants/generic-constants';

/**
 * Status of an access request (matches backend string values).
 */
export type AccessRequestStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';

/**
 * Corp segment structure as stored in the backend JSONB.
 * Example: [{ segment_name: "cost_center", segment_value: ["7128", "5174"] }]
 */
export interface CorpSegmentEntry {
  segment_name: string;
  segment_value: string[];
}

/**
 * A single access request row from the backend.
 * One row per (user × business group).
 */
export interface AccessRequestEntity {
  request_id: string;
  user_alias: string; // the beneficiary (on_behalf_of)
  user_name: string;
  data_classification_id: number;
  business_group_name: string;
  requested_role: string;
  requested_corp_segments: string; // AWSJSON string — parse with JSON.parse() to get CorpSegmentEntry[]
  granted_corp_segments: string | null; // AWSJSON string or null
  justification: string;
  status: string; // Pending | Approved | Rejected | Cancelled
  requester_alias: string;
  requester_name: string;
  rejection_reason: string | null;
  reviewer_alias: string | null;
  reviewer_name: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Helper to parse the JSONB segments string into typed array.
 */
export const parseCorpSegments = (json: string | null): CorpSegmentEntry[] => {
  if (!json) return [];
  try {
    return JSON.parse(json) as CorpSegmentEntry[];
  } catch {
    return [];
  }
};

/**
 * Extract flat cost center codes from corp segments.
 */
export const getCostCentersFromSegments = (segments: CorpSegmentEntry[]): string[] => {
  const entry = segments.find((s) => s.segment_name === 'cost_center');
  return entry?.segment_value ?? [];
};

/**
 * Extract flat product line codes from corp segments.
 */
export const getProductLinesFromSegments = (segments: CorpSegmentEntry[]): string[] => {
  const entry = segments.find((s) => s.segment_name === 'product_line');
  return entry?.segment_value ?? [];
};

// ─── Legacy types (used by the form layer — will be refactored later) ────────

/**
 * The role and segments being requested for one Business Group (form-layer only).
 */
export interface BusinessGroupAccessRequest {
  data_classification_id: number;
  data_classification_short_description: string;
  business_group_name: string;
  user_type: UserType;
  cost_centers: string[];
  product_lines: string[];
}
